export type Variant = {
  id: number;
  title: string;
  shopify_variant_id: number;
  parent_shopify_variant_id: number;
  available: boolean;
  price: number;
  compare_at_price: number;
  featured_image: {
    src: string;
    aspect_ratio: string;
  };
  image: string;
};

export type CartUpdates = Record<string, number>;

export type GhostProduct = {
  shopify_product_id: number;
  variants?: Variant[];
};

export type Product = {
  id: number;
  title?: string;
  shopify_product_id: number;
  ghost_product?: GhostProduct;
  variants?: Variant[];
  handle: string;
  shopify_product_handle: string;
  available: boolean;
  shopify_product_variants: number[];
  featured_image: string;
  loaded?: boolean;
  url?: string;
  media: {
    src: string;
    aspect_ratio: string;
  }[];
};

export type ShopifyVariantId = number;

export type OfferType =
  | "ProductOffer"
  | "MultipleProductOffer"
  | "FreeShippingOffer"
  | "ServiceOffer";

// offers saved in localstorage
export type OfferStorage = {
  offerId: number;
  productId: number | null;
  productVariantId?: number;
  shopifyVariantId?: number | null;
  productHandle: string;
  threshold: number | null;
  type?: OfferType;
  rejected: boolean;
  cartToken: string;
  thresholdMode: Offer["threshold_mode"];
  targetShopifyCollection_id: Offer["target_shopify_collection_id"];
  /**
   * Quantity  = 0:  the gift quantity matches the purchased product quantity
   Quantity  = 1:  the gift is always a single product
   Quantity >= 2:  custom quantity
   */
  giftQuantity?: number;
};

// modified offers for ui purposes
export type OfferUI = Offer & {
  justAdded: boolean;
};

// offers coming from the API response (api/offers)
export type Offer = {
  id: number;
  type?: OfferType;
  ghost_product?: GhostProduct;
  shopify_product_id: number | null;
  original_price?: number;
  threshold: number | null;
  gift_link_name?: string;
  end_datetime?: number;
  start_datetime?: number;
  shopify_product_handle?: string;
  added?: boolean;
  products?: Product[];
  rejected?: boolean;
  target_shopify_collection_id: number;
  shopify_product_variants: number[];
  target_min_quantity: number;
  threshold_mode: string;
  spend: number;
  target_shopify_product_id: number;
  shopify_product_variant_id?: number;
  target_shopify_collection_handle?: string;
  target_shopify_product_handle?: string;
  target_shopify_product_name?: string;
  target_shopify_collection_name?: string;
  target_all_variants_selected: boolean;
  target_shopify_product_variants: number[];
  add: boolean;
  title_condition_not_met: string;
  subtitle_condition_not_met?: string;
  display_name?: string;
  subtitle_condition_met_product_selection?: string;
  display_product_names_as_links?: string;
  /**
   * Quantity  = 0:  the gift quantity matches the purchased product quantity
   Quantity  = 1:  the gift is always a single product
   Quantity >= 2:  custom quantity
   */
  gift_quantity?: number;
  custom_wording_metaobject_id?: number;
  title_empty_cart?: string;
  subtitle_condition_met?: string;
  title_condition_met_product_selection?: string;
};

export type Updates = {
  offers?: Offer[];
};

export type Settings = {
  ajax_cart_watcher_selector?: string;
  align_left?: boolean;
  and_select_one_of_the_free_gifts?: string;
  background_color?: string;
  bottom_spacing?: number;
  buy_any_product_from_the_collection?: string;
  buy_product?: string;
  buy_reqproductq_products_from_the_collection?: string;
  buy_reqproductq_products?: string;
  custom_css?: string;
  custom_free_gift?: string;
  discount_adjust_spending?: boolean;
  discount_correct_info?: string;
  discount_incorrect_info?: string;
  discount_page_header?: string;
  discount_prompt?: string;
  discount_section_button?: string;
  discount_section_discount?: string;
  discount_title?: string;
  discounts_enabled?: boolean;
  dont_miss_your_reward?: string;
  free_shipping?: string;
  gift_only_highest_gift?: boolean;
  gift_product_prefix?: string;
  open_after_receiving_gift?: boolean;
  open_on_first_visit_delay?: number;
  open_on_first_visit?: boolean;
  open_when_in_cart?: boolean;
  primary_color?: string;
  select_your_free_gift?: string;
  show_giftbox_icon?: boolean;
  side_spacing?: number;
  spend_remainingspend_in_the_collection?: string;
  spend_remainingspend_more_in_the_collection?: string;
  spend_remainingspend_more?: string;
  spend_remainingspend?: string;
  subtitle_condition_met?: string;
  subtitle?: string;
  subtotal?: string;
  title_condition_met?: string;
  title?: string;
  to_get_a_free_displayname?: string;
  to_get_a_free_gift?: string;
  to_get_free_shipping?: string;
  total?: string;
  use_ghost_products?: boolean;
};

export type EnhancedCb = {
  (mutationsList: unknown): void;
  enhancers: unknown[];
  inject(e: unknown): void;
};

export type CartItem = {
  id: number;
  variant_id: number;
  product_id: number;
  cart_line_item_id: number;
  spendings_per_line_item: number;
  discount_amount: number;
  final_line_price: number;
  price: number;
  quantity: number;
  product_type: string;
};

type DiscountSpendings = {
  spendings_per_line_item: CartItem[];
  total_spending: number;
};

export type Cart = {
  items: CartItem[];
  discountSpendings: DiscountSpendings;
  total_price: number;
  token: string;
  item_count: number;
  currency: string;
  original_total_price: number;
  total_discount: number;
};

export type CollectionsInCart = number[][];

export type OffersResponse = {
  offers: OfferUI[];
  settings: Settings;
  error?: string;
  remainingOffers: number;
};

export interface GhostProductHandlePairs {
  [key: string]: string | null;
}

export type AppInfoResponse = {
  config: string | null;
  gp_handle_pairs: GhostProductHandlePairs | null;
  injection_method: string;
  gp_hiding_in_collections_disabled: boolean;
  storefront_access_token: string;
};

export type Selector = { name?: string; value: string } | string;

export type Callback<T> = (arg: T) => void;

export type Button = HTMLButtonElement;

export type ButtonPair = {
  hiddenButton: HTMLButtonElement;
  clonedButton: HTMLButtonElement;
};

export type StoredGiftLink = {
  giftLink: string;
  lastTimeUsed: Date;
};

export enum ScreenType {
  OFFERS = "offers",
  DISCOUNT = "discount",
}
